// src/components/LoginModal.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField, 
  Typography,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { themeColors } from '../styles/theme';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import EditLocationOutlinedIcon from '@mui/icons-material/EditLocationOutlined';

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

const LoginModal = ({ open, onClose }: LoginModalProps) => {
  const { signInWithGoogle, signInWithEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleEmailLogin = async () => {
    if (!email) {
      setMessage('Please enter your email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      setMessage('');
      await signInWithEmail(email);
      setEmailSent(true);
      setMessage('Check your email for a login link');
    } catch (error) {
      console.error('Error during email login:', error);
      setMessage('Failed to send the login link. Please try again.');
      setEmailSent(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      await signInWithGoogle();
      // This will redirect to Google
    } catch (error) {
      console.error('Error during Google login:', error);
      setMessage('Failed to sign in with Google. Please try again.');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setEmail('');
    setMessage('');
    setEmailSent(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Sign in to Suntap
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Create a free account to unlock all features
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Benefits Section */}
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Why create an account?
          </Typography>
          <List dense sx={{ py: 0 }}>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <StarOutlineIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Earn points for each garden you submit" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <EmojiEventsOutlinedIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Compete on community leaderboards" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <EditLocationOutlinedIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Edit and manage your submissions" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleOutlineIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Track your contribution history" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </Box>

        {/* Sign in options */}
        <Box sx={{ my: 2 }}>
          <Button 
            fullWidth 
            variant="contained" 
            onClick={handleGoogleLogin}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
            sx={{ 
              mb: 2, 
              bgcolor: themeColors.blue,
              textTransform: 'none',
              py: 1.5,
              '&:hover': {
                bgcolor: '#2c5289'
              }
            }}
          >
            Continue with Google
          </Button>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
          
          {!emailSent ? (
            <>
              <TextField
                label="Email address"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="yourname@example.com"
                disabled={isLoading}
                margin="normal"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleEmailLogin();
                  }
                }}
                sx={{ mb: 2 }}
              />
              
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={handleEmailLogin}
                disabled={isLoading || !email}
                startIcon={isLoading ? <CircularProgress size={20} /> : <EmailIcon />}
                sx={{ 
                  textTransform: 'none',
                  py: 1.5
                }}
              >
                Send Magic Link
              </Button>
            </>
          ) : (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                We've sent a magic link to <strong>{email}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Click the link in your email to sign in. You can close this window.
              </Typography>
            </Alert>
          )}
          
          {message && !emailSent && (
            <Alert 
              severity={message.includes('Check your email') ? 'success' : 'error'} 
              sx={{ mt: 2 }}
            >
              {message}
            </Alert>
          )}
        </Box>

        {/* Terms and Privacy */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            textAlign: 'center',
            mt: 2
          }}
        >
          By signing in, you agree to our{' '}
          <Link 
            component={RouterLink} 
            to="/terms-of-use" 
            onClick={handleClose}
            sx={{ color: themeColors.blue }}
          >
            Terms of Use
          </Link>
          {' '}and{' '}
          <Link 
            component={RouterLink} 
            to="/privacy-policy" 
            onClick={handleClose}
            sx={{ color: themeColors.blue }}
          >
            Privacy Policy
          </Link>
        </Typography>

        {/* Anonymous user note */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            textAlign: 'center',
            mt: 2,
            fontStyle: 'italic'
          }}
        >
          You can continue using Suntap without an account, but some features will be limited.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={isLoading}
          fullWidth
          variant="text"
        >
          {emailSent ? 'Close' : 'Continue without account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginModal;